import { useEffect, useState } from "react";
import { Link } from "wouter";
import { CheckCircle2, MailX } from "lucide-react";
import { unsubscribeEmail } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export default function Unsubscribe() {
  const [token] = useState(() => new URLSearchParams(window.location.search).get("token") ?? "");
  const preference = token.match(/^v1\.\d+\.(dailySummary|lowStock)\./)?.[1];
  const label = preference === "dailySummary" ? "daily business summaries" : "low-stock alerts";
  const [status, setStatus] = useState<"ready" | "saving" | "done" | "error">("ready");

  // Keep the signed link out of the address bar after the page has captured it.
  useEffect(() => {
    if (token) window.history.replaceState(window.history.state, "", window.location.pathname);
  }, [token]);

  const confirm = async () => {
    setStatus("saving");
    try {
      await unsubscribeEmail({ token });
      setStatus("done");
    } catch {
      setStatus("error");
    }
  };

  return (
    <main className="min-h-screen bg-background flex flex-col items-center justify-center px-4 py-12">
      <Link href="/" className="mb-8 text-xl font-bold text-primary">RetailPulse</Link>
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          {status === "done"
            ? <CheckCircle2 className="mx-auto mb-2 h-10 w-10 text-green-600" aria-hidden="true" />
            : <MailX className="mx-auto mb-2 h-10 w-10 text-primary" aria-hidden="true" />}
          <CardTitle>
            {status === "done" ? "You're unsubscribed" : "Email preferences"}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5 text-center text-sm">
          {status === "done" ? (
            <p>You won't receive {label} anymore. You can turn them on again in Settings.</p>
          ) : !preference ? (
            <p className="text-destructive">This unsubscribe link is invalid. Check the link in your email or update your preferences in Settings.</p>
          ) : (
            <>
              <p>Stop receiving {label}? This will not affect security, receipts, order or subscription emails.</p>
              {status === "error" && (
                <p role="alert" className="text-destructive">
                  We couldn't update your preference. The link may be invalid or your email may have changed. Please try again.
                </p>
              )}
              <Button type="button" onClick={() => void confirm()} disabled={status === "saving"} className="w-full">
                {status === "saving" ? "Updating…" : `Unsubscribe from ${label}`}
              </Button>
            </>
          )}
          <p className="text-muted-foreground">
            <Link href="/login" className="text-primary underline">Sign in</Link> to manage all your email preferences.
          </p>
        </CardContent>
      </Card>
    </main>
  );
}